// "Your authenticator app" on Your account (#746, #804): whether one is set up,
// setting one up where there is none (#875, `EnrolAuthenticator.tsx`), and
// replacing a lost one.
//
// **The act is three calls, in this order** (`api/authenticator.ts`): a step-up
// for `mfa_replace`, the start with its grant, which answers with the new
// secret, and the verify with a code from the new app, which answers with the
// new recovery codes. A person who has lost the app proves it with a recovery
// code: `mfa_replace` is not among the purposes the dialog withholds one from
// (`StepUp.tsx::RECOVERY_REFUSED_PURPOSES`), and it is the only factor act that
// is not.
//
// **Nothing is replaced until the verify answers.** The server holds the new
// secret beside the old one, so a person who stops half-way, or types a wrong
// code, is told the old app still works. The recovery codes the verify answers
// with are drawn once, until the person says they have saved them.
//
// The authenticator belongs to the user, not to the workspace being read, so
// the section does not depend on a context.

import { useCallback, useEffect, useRef, useState } from "react";

import {
  MFA_REPLACE_PURPOSE,
  type AuthenticatorActions,
  type Replacement,
} from "../api/authenticator";
import { ApiError, messageOf } from "../api/http";
import { stepUpFactorsOf, type MfaStatus } from "../api/passkeys";
import { Chip, Field, Notice } from "./chrome";
import { EnrolAuthenticator } from "./EnrolAuthenticator";
import { useDropOnPageHide } from "./pageHide";
import { StepUpDialog, useStepUpAttempt } from "./StepUp";

type Stage =
  | { kind: "idle" }
  | { kind: "prove" }
  | { kind: "verify"; replacement: Replacement }
  | { kind: "done"; codes: string[] };

function Prove({
  actions,
  mfa,
  onStarted,
  onCancel,
}: {
  actions: AuthenticatorActions;
  mfa: MfaStatus;
  onStarted: (replacement: Replacement) => void;
  onCancel: () => void;
}) {
  // Each attempt spends its proof: a recovery code is single-use (#729).
  const { proof, attempt, unproven, setProof, spent } = useStepUpAttempt();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const start = async () => {
    if (proof === null) return;
    setProblem(null);
    setBusy(true);
    try {
      const grant = await actions.stepUp(proof, MFA_REPLACE_PURPOSE);
      onStarted(await actions.begin(grant));
    } catch (failure) {
      setProblem(`${messageOf(failure)} Nothing was replaced.`);
    } finally {
      setBusy(false);
      spent();
    }
  };

  return (
    <div className="panel__body stack stack--tight" data-testid="replace-authenticator">
      <h3>Replace your authenticator app</h3>
      <p className="muted">
        If you no longer have your authenticator app, confirm it is you with one of your recovery
        codes. You then set up a new app, and once it is verified your old app and old recovery
        codes stop working and every other session of yours is signed out.
      </p>
      {problem === null ? null : <Notice tone="bad">{problem}</Notice>}
      <StepUpDialog
        key={attempt}
        purpose={MFA_REPLACE_PURPOSE}
        factors={stepUpFactorsOf(mfa)}
        disabled={busy}
        beginPasskey={actions.beginPasskey}
        onProofChange={setProof}
        onError={setProblem}
      />
      <div className="actions">
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy || unproven}
          onClick={() => void start()}
        >
          {busy ? "Starting…" : "Continue"}
        </button>
        <button type="button" className="btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** The wire code of a refusal (`detail.error`), or null. */
const codeOf = (failure: unknown): string | null => {
  if (!(failure instanceof ApiError)) return null;
  const detail = (failure.body as { detail?: { error?: unknown } } | null)?.detail;
  return typeof detail?.error === "string" ? detail.error : null;
};

/**
 * Why a verify ended the replacement rather than refusing one code: the wrong
 * code that spent its last attempt, or a replacement no longer pending (it
 * expired, with the step-up that started it). Either way the secret on screen
 * finishes nothing, so the person starts again.
 */
function endedBy(failure: unknown): string | null {
  if (codeOf(failure) === "replacement_exhausted") {
    return "Too many wrong codes. Start the replacement again.";
  }
  if (failure instanceof ApiError && failure.status === 404) {
    return "This replacement has ended. Start the replacement again.";
  }
  return null;
}

function Verify({
  actions,
  replacement,
  onDone,
  onEnded,
  onCancel,
}: {
  actions: AuthenticatorActions;
  replacement: Replacement;
  onDone: (codes: string[]) => void;
  /** The replacement is over and nothing was replaced; `why` is what to tell the person. */
  onEnded: (why: string) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  // Leaving the page ends the replacement as Cancel does, secret and all (#929).
  useDropOnPageHide(onCancel);

  const verify = async () => {
    setProblem(null);
    setBusy(true);
    try {
      onDone(await actions.verify(code.trim()));
    } catch (failure) {
      const why = endedBy(failure);
      if (why !== null) {
        onEnded(why);
        return;
      }
      setProblem(
        `${messageOf(failure)} Nothing was replaced; your old authenticator app still works.`,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel__body stack stack--tight" data-testid="replace-authenticator">
      <h3>Set up your new authenticator app</h3>
      <p>
        Add this key to your new app, or open the link on the device that holds it, then enter
        the code it shows.
      </p>
      <p>
        <code className="secret">{replacement.secret}</code>
      </p>
      <p>
        <a href={replacement.qr_uri}>Open in an authenticator app</a>
      </p>
      <Notice>
        Until the new app is verified, nothing has changed: your old authenticator app still works.
      </Notice>
      {problem === null ? null : <Notice tone="bad">{problem}</Notice>}
      <Field
        id="replace-authenticator-code"
        label="A code from your new authenticator app"
        inputMode="numeric"
        autoComplete="one-time-code"
        disabled={busy}
        value={code}
        onChange={(event) => setCode(event.target.value)}
      />
      <div className="actions">
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy || code.trim() === ""}
          onClick={() => void verify()}
        >
          {busy ? "Verifying…" : "Replace authenticator"}
        </button>
        <button type="button" className="btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function Done({ codes, onSaved }: { codes: string[]; onSaved: () => void }) {
  // Leaving the page puts the codes away as the button does (#929).
  useDropOnPageHide(onSaved);
  return (
    <div className="panel__body stack stack--tight" data-testid="replaced-authenticator">
      <Notice tone="good">
        Your authenticator app was replaced. Your old app and your old recovery codes no longer
        work, and your other sessions were signed out.
      </Notice>
      <p>
        Your new recovery codes. Save them somewhere safe now: they are shown only once.
      </p>
      <ul className="recovery-codes">
        {codes.map((code) => (
          <li key={code}>
            <code>{code}</code>
          </li>
        ))}
      </ul>
      <div className="actions">
        <button type="button" className="btn btn--primary" onClick={onSaved}>
          I have saved these codes
        </button>
      </div>
    </div>
  );
}

export function YourAuthenticator({ actions }: { actions: AuthenticatorActions }) {
  const [mfa, setMfa] = useState<MfaStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>({ kind: "idle" });
  const [abandoned, setAbandoned] = useState(false);
  /** A setup that has enabled the app and still shows its recovery codes (#875). */
  const [enrolling, setEnrolling] = useState(false);
  const heading = useRef<HTMLHeadingElement | null>(null);
  /** Why the last setup ended because another session changed it (#875), until another starts. */
  const [elsewhere, setElsewhere] = useState<string | null>(null);
  /** Why the last replacement ended without replacing anything, shown until another starts. */
  const [ended, setEnded] = useState<string | null>(null);

  // The console rebuilds its actions every render; the status is read on mount
  // and after a replacement, not on every render.
  const live = useRef(actions);
  live.current = actions;
  const refresh = useCallback(async (): Promise<MfaStatus | null> => {
    try {
      const read = await live.current.readMfa();
      setMfa(read);
      setError(null);
      return read;
    } catch (failure) {
      setError(messageOf(failure));
      return null;
    }
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const cancel = () => {
    setStage({ kind: "idle" });
    setAbandoned(true);
  };

  return (
    <section className="panel" id="authenticator" aria-label="Your authenticator app">
      <div className="panel__head">
        <h2 ref={heading} tabIndex={-1}>
          Your authenticator app
        </h2>
      </div>
      <div className="panel__body stack stack--tight">
        {error === null ? null : <Notice tone="bad">{error}</Notice>}
        {error !== null && mfa === null ? (
          <div className="actions">
            <button type="button" className="btn" onClick={() => void refresh()}>
              Try again
            </button>
          </div>
        ) : null}
        {mfa === null && error === null ? (
          <p className="muted">Reading your authenticator app…</p>
        ) : null}
        {mfa === null ? null : (
          <p className="row row--wrap">
            {mfa.totp_enabled ? <Chip tone="good">Enrolled</Chip> : <Chip>Not set up</Chip>}
            <span className="muted">
              {mfa.totp_enabled
                ? "Signing in asks for a code from your authenticator app."
                : "Signing in does not ask for a code from an authenticator app."}
            </span>
          </p>
        )}
        {elsewhere === null ? null : <Notice>{elsewhere}</Notice>}
        {ended !== null && stage.kind === "idle" ? <Notice tone="bad">{ended}</Notice> : null}
        {abandoned && stage.kind === "idle" ? (
          <Notice>Nothing was replaced; your old authenticator app still works.</Notice>
        ) : null}
      </div>
      {mfa !== null && (!mfa.totp_enabled || enrolling) ? (
        <EnrolAuthenticator
          actions={actions}
          mfa={mfa}
          onEnrolled={() => {
            setEnrolling(true);
            void refresh();
          }}
          onFinished={() => {
            setEnrolling(false);
            heading.current?.focus();
          }}
          onChanged={(why) => {
            setElsewhere(why);
            // Still not set up, the setup's own button has the focus; turned on
            // elsewhere, the setup is gone, so the panel's heading takes it.
            void refresh().then((read) => {
              if (read?.totp_enabled) heading.current?.focus();
            });
          }}
          onBegin={() => setElsewhere(null)}
        />
      ) : null}
      {mfa === null || !mfa.totp_enabled || enrolling ? null : stage.kind === "prove" ? (
        <Prove
          actions={actions}
          mfa={mfa}
          onCancel={cancel}
          onStarted={(replacement) => setStage({ kind: "verify", replacement })}
        />
      ) : stage.kind === "verify" ? (
        <Verify
          actions={actions}
          replacement={stage.replacement}
          onCancel={cancel}
          onEnded={(why) => {
            setStage({ kind: "idle" });
            setEnded(why);
          }}
          onDone={(codes) => {
            setStage({ kind: "done", codes });
            void refresh();
          }}
        />
      ) : stage.kind === "done" ? (
        <Done codes={stage.codes} onSaved={() => setStage({ kind: "idle" })} />
      ) : (
        <div className="panel__body actions">
          <button
            type="button"
            className="btn"
            onClick={() => {
              setAbandoned(false);
              setEnded(null);
              setElsewhere(null);
              setStage({ kind: "prove" });
            }}
          >
            Lost your authenticator? Replace it
          </button>
        </div>
      )}
    </section>
  );
}
